import type { DocNode } from '@atlaskit/adf-schema/doc';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { ConfluenceUpdatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { getGeneratedEditVisualBlockCounts } from '@atlassian/rovo-content-bridge-api-commands/commands/generated-edit-visual-block-counts';

import type {
	DocumentUpdateCommandProcessor,
	FinalVisualBlockCounts,
} from '../stream-processors/update-command-processor';
import type { AbortOptions, AbortReason } from '../types/abort-reason';
import type { AIFC_ADF_UPDATE_KEY } from '../types/command-manager';
import { getAgentUpdateMetadata } from '../utils/get-agent-attribution';
import type { PassThroughCommandProps } from './command-handler';
import { StreamingCommandHandler } from './streaming-command-handler';

type AdfUpdatePayload = ConfluenceUpdatePageCommandProperties & {
	key: typeof AIFC_ADF_UPDATE_KEY;
};

export class UpdateCommandHandler extends StreamingCommandHandler<ConfluenceUpdatePageCommandProperties> {
	/** Visual block counts from this stream's final payload, handed over when the session ends. */
	private finalVisualBlockCounts: FinalVisualBlockCounts | undefined;

	constructor(
		props: PassThroughCommandProps,
		private updateCommandProcessor: DocumentUpdateCommandProcessor,
	) {
		super(props, 'update');
	}

	protected startAction(strictFifo: boolean, onExecutionTimeout?: () => void): void {
		this.updateCommandProcessor.startAction(strictFifo, onExecutionTimeout);
	}

	process(payload: AdfUpdatePayload): void {
		const { agentInfo, agentAttribution } = getAgentUpdateMetadata(payload);

		// The backend sends the edit's visual block counts on the final payload only. Keep them so
		// they are handed over with the session end, which adds them to the edit's accepted event.
		if (payload.isStreamComplete && payload.singleInstrumentationId) {
			const visualBlockCounts = getGeneratedEditVisualBlockCounts(payload.data);
			this.finalVisualBlockCounts =
				Object.keys(visualBlockCounts).length > 0
					? { singleInstrumentationId: payload.singleInstrumentationId, visualBlockCounts }
					: undefined;
		}

		const isBrowserEligibleCompletion =
			isExperimentEnabled('platform_editor_ai_tool_call_context') &&
			payload.isStreamComplete &&
			(payload.data as typeof payload.data & { appliedServerSide?: boolean }).appliedServerSide ===
				false;
		// `creationExperience` stays at its positional slot; browser eligibility follows it.
		const trailingArgs: [creationExperience?: string, isBrowserEligibleCompletion?: true] =
			isBrowserEligibleCompletion
				? [payload.creationExperience, true]
				: payload.creationExperience !== undefined
					? [payload.creationExperience]
					: [];
		this.updateCommandProcessor.streamChunk(
			payload.data.toolCalls,
			payload.isStreamComplete,
			payload.traceId,
			payload.singleInstrumentationId,
			agentInfo,
			payload.isInlineEdit,
			agentAttribution,
			payload.isChatEdit,
			payload.cwrRemixInvokedFrom,
			...trailingArgs,
		);
	}

	async finishAction(): Promise<DocNode | undefined> {
		const finalVisualBlockCounts = this.finalVisualBlockCounts;
		this.finalVisualBlockCounts = undefined;
		await this.updateCommandProcessor.finish(finalVisualBlockCounts);
		return undefined;
	}

	abort(reason?: AbortReason, options?: AbortOptions): void {
		this.finalVisualBlockCounts = undefined;
		this.updateCommandProcessor.abort(reason, options);
	}
}
